import axios from 'axios';
import { toast } from 'sonner';

const TOKEN_KEY = 'pou_access_token';

// Helpers exported for use in AuthContext / Demo quick-login
export const setStoredToken = (token) => {
  if (token) {
    try { localStorage.setItem(TOKEN_KEY, token); } catch { /* sandboxed */ }
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    try { localStorage.removeItem(TOKEN_KEY); } catch { /* sandboxed */ }
    delete axios.defaults.headers.common['Authorization'];
  }
};

export const getStoredToken = () => {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
};

// Restore token on app boot (page reload / new tab)
const bootToken = getStoredToken();
if (bootToken) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${bootToken}`;
}

// Make sure every request includes credentials (cookie fallback) — harmless if cookies are blocked.
axios.defaults.withCredentials = true;

// ----------------------------------------------------------------------------
// DEFENSIVE: Cross-subdomain redirect protection.
//
// Some deployment pipelines may bake `REACT_APP_BACKEND_URL=https://www.<host>`
// into the production bundle (e.g. when a "Link domain" flow picks the `www.`
// variant). If the user lands on the apex (no `www.`), every axios request
// becomes cross-origin, the host returns 308 Permanent Redirect, and the
// browser refuses to follow it on CORS-preflighted credentialed POSTs.
//
// We rewrite any outgoing URL whose hostname starts with `www.` to its apex
// when the current page is on the apex. This makes API calls same-origin and
// bypasses the redirect entirely. Safe no-op if the URL is already correct.
// ----------------------------------------------------------------------------
try {
  const pageOrigin = window.location.origin || '';
  const pageHost = window.location.hostname || '';
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  // Sanity check: warn loudly if env-baked URL doesn't agree with page origin.
  if (envUrl) {
    try {
      const envHost = new URL(envUrl).hostname;
      const apexHost = pageHost.replace(/^www\./, '');
      if (envHost !== pageHost && envHost.replace(/^www\./, '') === apexHost) {
        console.warn(
          `[axios-config] REACT_APP_BACKEND_URL (${envHost}) differs from page hostname (${pageHost}). ` +
          'Outgoing API calls will be auto-rewritten to use the page origin to avoid CORS 308.'
        );
      }
    } catch { /* ignore parse errors */ }
  }
  axios.interceptors.request.use((cfg) => {
    if (typeof cfg.url === 'string' && /^https?:\/\//i.test(cfg.url)) {
      try {
        const u = new URL(cfg.url);
        // If outgoing URL has www. but the current page does NOT, drop the www.
        if (u.hostname.startsWith('www.') && !pageHost.startsWith('www.') && u.hostname.replace(/^www\./, '') === pageHost) {
          u.hostname = u.hostname.replace(/^www\./, '');
          cfg.url = u.toString();
        }
        // Conversely: if outgoing URL is apex but the current page is on www, prefix the www.
        else if (!u.hostname.startsWith('www.') && pageHost.startsWith('www.') && pageHost === `www.${u.hostname}`) {
          u.hostname = `www.${u.hostname}`;
          cfg.url = u.toString();
        }
      } catch { /* ignore malformed URLs */ }
    }
    return cfg;
  });
} catch { /* SSR / non-browser environments */ }
// ----------------------------------------------------------------------------

// Paths where 401 should NOT trigger auto-redirect
const PUBLIC_AUTH_PATHS = [
  '/api/auth/me',
  '/api/auth/login',
  '/api/auth/register',
  '/api/auth/password-reset',
];

let redirectTimer = null;
const cancelRedirect = () => {
  if (redirectTimer) {
    clearTimeout(redirectTimer);
    redirectTimer = null;
  }
};

// Pages that should NOT be redirected away from on 401 (already-public flows).
const NO_REDIRECT_PATHS = [
  '/login',
  '/demo',
  '/',
  '/horizon',
  '/portal',
  '/buyer-register',
  '/supplier-register',
  '/partner-register',
  '/dsa-register',
  '/pre-register',
  '/register',
  '/forgot-password',
  '/pricing',
  '/privacy',
  '/terms',
  '/dpdp',
];

axios.interceptors.response.use(
  (response) => {
    cancelRedirect();
    return response;
  },
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    const isPublic = PUBLIC_AUTH_PATHS.some(p => url.includes(p));
    const pathname = window.location.pathname;
    const onPublicPage = NO_REDIRECT_PATHS.some(p => p === '/' ? pathname === '/' : pathname.startsWith(p));
    if (
      status === 401 &&
      !isPublic &&
      !onPublicPage &&
      !redirectTimer
    ) {
      setStoredToken(null);
      toast.error('Your session has expired. Please log in again.');
      redirectTimer = setTimeout(() => {
        if (!window.location.pathname.startsWith('/login')) {
          window.location.href = '/login';
        }
        redirectTimer = null;
      }, 1500);
    }
    return Promise.reject(error);
  }
);

export default axios;
